import * as React from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { TravelTrackerSubNav } from "@/components/travel_tracker/TravelTrackerSubNav"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { useBlockAutoReload } from "@/hooks/use-block-auto-reload"
import type { PageProps } from "@/types/inertia"
import type {
  PeriodOption,
  TravelDay,
  TravelLocationOption,
  TravelSubject,
} from "@/types/travel_tracker"

const DESCRIPTION =
  "Record where you worked from on each day of a two-week period. Pick a period, choose a place for any day, and save — clearing a day removes it."

/** { "2026-08-16": "12", ... } — the picker speaks strings, Rails wants ids. */
type DaySelections = Record<string, string>

/**
 * The option that clears a day. A day with no location has no record at all, so
 * this is the absence of a place rather than a place, and it is never a seeded
 * location. The legacy form spelled it " --- ".
 */
const CLEAR = { value: "", label: "No travel recorded" }

function initialSelections(days: TravelDay[]): DaySelections {
  return Object.fromEntries(
    days.map((day) => [day.date, day.location_id ? String(day.location_id) : ""]),
  )
}

export default function TravelTrackerForm({
  period,
  periods,
  locations,
  days,
  can_edit,
  subject,
  fillable_for,
}: {
  period: PeriodOption
  periods: PeriodOption[]
  locations: TravelLocationOption[]
  days: TravelDay[]
  /** False with Read only: the days can be read and the period changed, nothing saved. */
  can_edit: boolean
  /** Whose report is on screen: yours, or somebody who named you their support. */
  subject: TravelSubject
  /** You, plus everyone who has named you. One entry means no switcher. */
  fillable_for: TravelSubject[]
}) {
  const errors = usePage<PageProps>().props.errors ?? {}
  const readOnly = !can_edit
  const [pending, setPending] = React.useState<string | null>(null)
  const [pendingSubject, setPendingSubject] = React.useState<string | null>(null)
  // Every link and the save carry whose report this is, or work done for
  // somebody else would land on your own.
  const forParam: Record<string, string> = subject.is_self
    ? {}
    : { for: String(subject.id) }
  const trackerPath = (params: Record<string, string>) =>
    "/travel_tracker?" + new URLSearchParams({ ...forParam, ...params }).toString()
  const possessive = subject.is_self ? "your" : subject.name + "’s"

  const { data, setData, transform, patch, processing, isDirty } = useForm({
    period: period.value,
    // Grouped under one key rather than spread flat, so the fourteen days stay
    // indexable by date without fighting the form helper's typing.
    days: initialSelections(days),
    for: subject.is_self ? "" : String(subject.id),
  })

  useBlockAutoReload(isDirty)

  // A different period is a different page, so the day map has to be re-seeded
  // from props rather than keeping the one the form was built with.
  React.useEffect(() => {
    setData({
      period: period.value,
      days: initialSelections(days),
      for: subject.is_self ? "" : String(subject.id),
    })
    setPending(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.value, subject.id])

  // Code and label are the same string for all seventy-nine, so rendering both
  // would read "HQ — HQ". The clear option leads, so emptying a day is one click
  // rather than a hunt for the way out.
  const options = React.useMemo(
    () => [
      CLEAR,
      ...locations.map((location) => ({
        value: String(location.id),
        label:
          location.code === location.label
            ? location.code
            : location.code + " — " + location.label,
      })),
    ],
    [locations],
  )

  const periodOptions = React.useMemo(
    () => periods.map((option) => ({ value: option.value, label: option.label })),
    [periods],
  )

  const recordedDays = Object.values(data.days).filter(Boolean).length

  const setDay = (date: string, value: string) =>
    setData("days", { ...data.days, [date]: value })

  // Changing the period navigates on an untouched form. With unsaved work it
  // does not: silently discarding what somebody just picked is worse than asking
  // them to save or say so first.
  const choosePeriod = (value: string) => {
    if (value === data.period) return
    if (isDirty) {
      setPending(value)
      return
    }
    router.get("/travel_tracker", { period: value, ...forParam }, { preserveState: false })
  }

  // Switching whose report is on screen. Unsaved work is kept for the same
  // reason changing the period keeps it: discarding it silently is worse.
  const chooseSubject = (value: string) => {
    if (value === String(subject.id)) return
    if (isDirty) {
      setPendingSubject(value)
      return
    }
    const chosen = fillable_for.find((person) => String(person.id) === value)
    router.get("/travel_tracker", chosen?.is_self ? {} : { for: value }, {
      preserveState: false,
    })
  }

  const pendingLabel = periods.find((option) => option.value === pending)?.label
  const pendingPerson = fillable_for.find((person) => String(person.id) === pendingSubject)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (readOnly) return
    transform((form) => ({ travel_tracker: form }))
    patch(trackerPath({}), { preserveScroll: true })
  }

  const weekOne = days.filter((day) => day.week === 1)
  const weekTwo = days.filter((day) => day.week === 2)

  return (
    <>
      <Head title="Travel Tracker">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Travel Tracker" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="Travel Tracker"
          description={
            subject.is_self
              ? "Where you worked from, day by day."
              : "Where " + subject.name + " worked from, day by day. You are filling this out for them."
          }
          tabs={<TravelTrackerSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          {errors.base && (
            <div className="callout callout-danger mb-4" role="alert">
              {errors.base}
            </div>
          )}

          {readOnly && (
            <div className="callout callout-signal mb-4" role="status">
              <p className="text-sm">
                You have read-only access to Travel Tracker. You can look through{" "}
                {possessive} recorded days, but not change them.
              </p>
            </div>
          )}

          <form onSubmit={submit}>
            {fillable_for.length > 1 && (
              <div className="mb-6 max-w-sm">
                <label htmlFor="subject">Whose report</label>
                <div className="mt-1">
                  <Combobox
                    id="subject"
                    options={fillable_for.map((person) => ({
                      value: String(person.id),
                      label: person.is_self ? "My report" : person.name,
                    }))}
                    value={String(subject.id)}
                    onChange={chooseSubject}
                    searchPlaceholder="Find a person..."
                    triggerLabel="Choose whose report to fill out"
                  />
                </div>
              </div>
            )}

            {pendingSubject && (
              <div className="callout callout-signal mb-4" role="status">
                You have unsaved changes on {possessive} {period.label}. Save them first,
                or{" "}
                <Link
                  href={
                    pendingPerson?.is_self
                      ? "/travel_tracker"
                      : "/travel_tracker?for=" + pendingSubject
                  }
                  preserveState={false}
                  className="font-medium"
                >
                  open {pendingPerson?.is_self ? "your own report" : pendingPerson?.name} and
                  lose them
                </Link>
                .{" "}
                <button
                  type="button"
                  className="toggle-button ml-1"
                  onClick={() => setPendingSubject(null)}
                >
                  Stay here
                </button>
              </div>
            )}

            <div className="max-w-sm">
              <label htmlFor="period">Period</label>
              <div className="mt-1">
                <Combobox
                  id="period"
                  options={periodOptions}
                  value={data.period}
                  onChange={choosePeriod}
                  searchPlaceholder="Jump to a month..."
                  triggerLabel="Choose a two-week period"
                />
              </div>
            </div>

            {pending && (
              <div className="callout callout-signal mt-4" role="status">
                You have unsaved changes on {period.label}. Save them first, or{" "}
                <Link
                  href={trackerPath({ period: pending })}
                  preserveState={false}
                  className="font-medium"
                >
                  open {pendingLabel} and lose them
                </Link>
                .{" "}
                <button
                  type="button"
                  className="toggle-button ml-1"
                  onClick={() => setPending(null)}
                >
                  Stay here
                </button>
              </div>
            )}

            <DayWeek
              heading="Week 1"
              days={weekOne}
              options={options}
              selections={data.days}
              onChange={setDay}
              disabled={readOnly}
              subject={subject}
            />
            <DayWeek
              heading="Week 2"
              days={weekTwo}
              options={options}
              selections={data.days}
              onChange={setDay}
              disabled={readOnly}
              subject={subject}
            />

            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-hairline pt-6">
              {!readOnly && (
                <Button type="submit" disabled={processing || !isDirty}>
                  {processing ? "Saving..." : "Save this period"}
                </Button>
              )}
              <p className="text-sm text-ink-muted">
                {recordedDays === 0
                  ? "No days recorded in this period yet."
                  : recordedDays + " of 14 days recorded."}
                {!readOnly && " Clearing a day removes it."}
              </p>
            </div>
          </form>
        </div>
      </AppShell>
    </>
  )
}

function DayWeek({
  heading,
  days,
  options,
  selections,
  onChange,
  disabled,
  subject,
}: {
  heading: string
  days: TravelDay[]
  options: { value: string; label: string }[]
  selections: DaySelections
  onChange: (date: string, value: string) => void
  disabled: boolean
  subject: TravelSubject
}) {
  return (
    /*
      Capped rather than left to fill the shell. At the shell's full width the
      row's justify-between threw the picker to the far right and left roughly
      500px of nothing between a day and its answer.
    */
    <section className="mt-8 max-w-xl">
      <h2>{heading}</h2>
      <ul className="mt-3 divide-y divide-hairline border-y border-hairline">
        {days.map((day) => (
          <li
            key={day.date}
            className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
          >
            <label htmlFor={"day-" + day.date}>
              {day.day_name}
              <span className="ml-2 font-normal text-ink-muted">{day.day_label}</span>
              {/* Set only when somebody else filed the day, so a person's own
                  report says nothing extra. */}
              {day.recorded_by && (
                <span className="block text-xs font-normal text-ink-muted">
                  Filed by {day.recorded_by}
                </span>
              )}
            </label>
            <div className="sm:w-80 sm:shrink-0">
              <Combobox
                id={"day-" + day.date}
                options={options}
                value={selections[day.date] ?? ""}
                disabled={disabled}
                onChange={(value) => onChange(day.date, value)}
                placeholder="No travel recorded"
                searchPlaceholder="Filter places..."
                triggerLabel={
                  (subject.is_self
                    ? "Where you worked from on "
                    : "Where " + subject.name + " worked from on ") +
                  day.day_name +
                  " " +
                  day.day_label
                }
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}
